import React from 'react';
import { Job } from '../types';
import GeographicComparison from './GeographicComparison';
import { useTooltip } from '../contexts/TooltipContext';

interface JobDescriptionProps {
  job: Job;
  onetData?: any; // ONET comprehensive data
}

type TabType = 'skills' | 'knowledge' | 'technology' | 'tools' | 'abilities';

export const JobDescription: React.FC<JobDescriptionProps> = ({ job, onetData }) => {
  // Tooltip context for global tooltip state
  const { setIsTooltipOpen } = useTooltip();

  // Add event listeners for tour-triggered clicks
  React.useEffect(() => {
    const handleTourIndustriesClick = () => {
      window.dispatchEvent(new CustomEvent('viewIndustries', { 
        detail: { socCode: job.socCode, jobTitle: job.title } 
      }));
    };

    const handleTourAllIndustriesClick = () => {
      window.dispatchEvent(new CustomEvent('viewAllIndustries', { 
        detail: { socCode: job.socCode, jobTitle: job.title } 
      }));
    };

    const handleTourFinancialAidClick = () => {
      window.dispatchEvent(new CustomEvent('showFinancialAid'));
    };

    window.addEventListener('tourClickIndustries', handleTourIndustriesClick);
    window.addEventListener('tourClickAllIndustries', handleTourAllIndustriesClick);
    window.addEventListener('tourClickFinancialAid', handleTourFinancialAidClick);

    return () => {
      window.removeEventListener('tourClickIndustries', handleTourIndustriesClick);
      window.removeEventListener('tourClickAllIndustries', handleTourAllIndustriesClick);
      window.removeEventListener('tourClickFinancialAid', handleTourFinancialAidClick);
    };
  }, [job.socCode, job.title]);
  const formatSalary = (salary?: number) => {
    if (!salary) return 'Not available';
    
    // Check if the salary seems too low (likely hourly wage instead of annual)
    // If salary is less than $1,000, it's probably hourly wage
    if (salary < 1000) {
      // Convert hourly wage to annual salary (assuming 40 hours/week, 52 weeks/year)
      const annualSalary = salary * 2080;
      return `$${Math.round(annualSalary).toLocaleString()}`;
    }
    
    // Additional check for very low values (definitely hourly wages)
    if (salary < 100) {
      const annualSalary = salary * 2080;
      return `$${Math.round(annualSalary).toLocaleString()}`;
    }
    
    return `$${Math.round(salary).toLocaleString()}`;
  };

  const formatEmployment = (employment?: number) => {
    if (!employment) return 'Not available';
    return Math.round(employment).toLocaleString();
  };

  const formatWage = (wage?: number) => {
    if (!wage) return 'Not available';
    
    // For hourly wages, values should typically be between $8-$200
    // If it's much higher, it might be annual salary that needs conversion
    if (wage > 200) {
      // Convert annual salary to hourly wage
      const hourlyWage = wage / 2080; // 40 hours/week * 52 weeks/year
      return `$${Math.round(hourlyWage).toLocaleString()}`;
    }
    
    return `$${Math.round(wage).toLocaleString()}`;
  };

  // Get hourly wage range from 2023 non-COL percentile data (10th-90th percentile)
  const getHourlyRange = () => {
    if (!job.hourlyPct10 || !job.hourlyPct90) return 'Not available';
    
    const minRange = Math.round(job.hourlyPct10);
    const maxRange = Math.round(job.hourlyPct90);
    
    return `$${minRange} - $${maxRange}`;
  };

  // Get annual salary range from 2023 non-COL percentile data (10th-90th percentile)
  const getAnnualSalaryRange = () => {
    if (!job.salaryPct10 || !job.salaryPct90) return 'Not available';
    
    const minRange = Math.round(job.salaryPct10);
    const maxRange = Math.round(job.salaryPct90);
    
    return `$${minRange.toLocaleString()} - $${maxRange.toLocaleString()}`;
  };

  const getEmploymentTrend = () => {
    if (!job.employmentChange) return 'neutral';
    return job.employmentChange > 0 ? 'positive' : 'negative';
  };

  const getEmploymentTrendIcon = () => {
    const trend = getEmploymentTrend();
    if (trend === 'positive') {
      return (
        <svg className="w-4 h-4 text-[#f2994a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    } else if (trend === 'negative') {
      return (
        <svg className="w-4 h-4 text-[#707070]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-[#a6a6a6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
      </svg>
    );
  };

  const [activeTab, setActiveTab] = React.useState<TabType>('skills');
  const [showMedianSalary, setShowMedianSalary] = React.useState(false);
  const [showColTooltip, setShowColTooltip] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  // Detect mobile viewport with debounced resize handler
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkMobile = () => {
      // Debounce resize events to prevent performance violations
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768);
      }, 100); // 100ms debounce
    };
    
    // Initial check
    checkMobile();
    
    window.addEventListener('resize', checkMobile, { passive: true });
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  // Close COL tooltip on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowColTooltip(false);
        setIsTooltipOpen(false);
      }
    };
    if (showColTooltip) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showColTooltip]);

  // Close tooltip when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[aria-label="Cost of Living Salary Information"]') || 
          target.closest('.col-tooltip-container')) {
        return;
      }
      setShowColTooltip(false);
      setIsTooltipOpen(false);
    };
    if (showColTooltip) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showColTooltip]);

  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);
  };

  // Dynamic data functions with proper ONET data structure handling
  const getSkillsData = () => {
    // Try onetData first (from separate API call)
    if (onetData?.skills) {
      // Handle both string array and object array formats
      if (Array.isArray(onetData.skills)) {
        if (typeof onetData.skills[0] === 'string') {
          return onetData.skills;
        } else {
          // Extract skill names from objects
          return onetData.skills.map((skill: any) => skill.name || skill);
        }
      }
    }
    // Try job.skills (from ONET enhancement in API)
    if (job.skills && Array.isArray(job.skills) && job.skills.length > 0) {
      return job.skills;
    }
    // Fallback to static data
    return [
      'Reading Comprehension', 'Active Listening', 'Writing', 'Speaking', 'Mathematics',
      'Science', 'Critical Thinking', 'Active Learning', 'Learning Strategies', 'Monitoring',
      'Social Perceptiveness', 'Coordination', 'Persuasion', 'Negotiation', 'Instructing',
      'Service Orientation', 'Complex Problem Solving', 'Operations Analysis', 'Technology Design',
      'Equipment Selection', 'Installation', 'Programming', 'Operations Monitoring', 'Operation and Control',
      'Equipment Maintenance', 'Troubleshooting', 'Repairing', 'Quality Control Analysis',
      'Judgment and Decision Making', 'Systems Analysis', 'Systems Evaluation', 'Time Management',
      'Management of Financial Resources', 'Management of Material Resources', 'Management of Personnel Resources'
    ];
  };

  const getKnowledgeData = () => {
    // Try onetData first (from separate API call)
    if (onetData?.knowledge) {
      // Handle both string array and object array formats
      if (Array.isArray(onetData.knowledge)) {
        if (typeof onetData.knowledge[0] === 'string') {
          return onetData.knowledge;
        } else {
          // Extract knowledge names from objects
          return onetData.knowledge.map((knowledge: any) => knowledge.name || knowledge);
        }
      }
    }
    // Try job.knowledge (from ONET enhancement in API)
    if (job.knowledge && Array.isArray(job.knowledge) && job.knowledge.length > 0) {
      return job.knowledge;
    }
    // Fallback to static data
    return [
      'Customer and Personal Service', 'Administration and Management', 'Sales and Marketing',
      'Economics and Accounting', 'Personnel and Human Resources', 'Production and Processing',
      'Mathematics', 'Computers and Electronics', 'English Language', 'Education and Training',
      'Public Safety and Security', 'Law and Government', 'Psychology', 'Sociology and Anthropology',
      'Medicine and Dentistry', 'Biology', 'Chemistry', 'Physics', 'Mechanical', 'Engineering and Technology',
      'Design', 'Building and Construction', 'Transportation', 'Geography', 'History and Archeology',
      'Philosophy and Theology', 'Foreign Language', 'Fine Arts', 'Communications and Media',
      'Telecommunications', 'Food Production', 'Chemistry', 'Public Safety and Security'
    ];
  };

  const getTechnologyData = () => {
    // Try onetData first (from separate API call)
    if (onetData?.technology) {
      // Handle both string array and object array formats
      if (Array.isArray(onetData.technology)) {
        if (typeof onetData.technology[0] === 'string') {
          return onetData.technology;
        } else {
          // Extract technology names from objects
          return onetData.technology.map((tech: any) => {
            if (typeof tech === 'string') return tech;
            return tech.software_name || tech.name || tech;
          });
        }
      }
    }
    // Try job.technology (from ONET enhancement in API)
    if (job.technology && Array.isArray(job.technology) && job.technology.length > 0) {
      return job.technology.map((tech: any) => {
        if (typeof tech === 'string') return tech;
        return tech.software_name || tech.name || tech;
      });
    }
    // Fallback to static data
    return [
      'Microsoft Excel', 'Microsoft Word', 'Microsoft PowerPoint', 'Microsoft Outlook',
      'Adobe Photoshop', 'Adobe Illustrator', 'QuickBooks', 'Salesforce', 'SAP',
      'Oracle Database', 'SQL Server', 'MySQL', 'PostgreSQL', 'MongoDB', 'Redis',
      'Docker', 'Kubernetes', 'AWS', 'Azure', 'Google Cloud Platform', 'Git',
      'GitHub', 'GitLab', 'Bitbucket', 'Jenkins', 'Jira', 'Confluence', 'Slack',
      'Zoom', 'Microsoft Teams', 'WebEx', 'Skype', 'Trello', 'Asana', 'Monday.com',
      'Notion', 'Figma', 'Sketch', 'InVision', 'Zeplin', 'Miro', 'Lucidchart',
      'Tableau', 'Power BI', 'Google Analytics', 'Mixpanel', 'Amplitude', 'Hotjar',
      'Optimizely', 'VWO', 'Google Optimize', 'Adobe Target', 'Segment', 'RudderStack',
      'Amplitude', 'Mixpanel', 'Google Analytics', 'Adobe Analytics', 'Hotjar',
      'FullStory', 'LogRocket', 'Sentry', 'DataDog', 'New Relic', 'Splunk',
      'Elasticsearch', 'Logstash', 'Kibana', 'Grafana', 'Prometheus', 'InfluxDB',
      'Kafka', 'RabbitMQ', 'Redis', 'Memcached', 'Varnish', 'CDN', 'CloudFlare',
      'AWS CloudFront', 'Azure CDN', 'Google Cloud CDN', 'Fastly', 'Akamai'
    ];
  };

  const getToolsData = () => {
    // Try onetData first (from separate API call)
    if (onetData?.tools) {
      // Handle both string array and object array formats
      if (Array.isArray(onetData.tools)) {
        if (typeof onetData.tools[0] === 'string') {
          return onetData.tools;
        } else {
          // Extract tool names from objects
          return onetData.tools.map((tool: any) => {
            if (typeof tool === 'string') return tool;
            return tool.tool_name || tool.name || tool;
          });
        }
      }
    }
    // Try job.tools (from ONET enhancement in API)
    if (job.tools && Array.isArray(job.tools) && job.tools.length > 0) {
      return job.tools.map((tool: any) => {
        if (typeof tool === 'string') return tool;
        return tool.tool_name || tool.name || tool;
      });
    }
    // Fallback to static data
    return [
      'Desktop computers', 'Laptop computers', 'Tablet computers', 'Smartphones',
      'Printers', 'Scanners', 'Barcode scanners', 'RFID readers', 'POS terminals',
      'Cash registers', 'Credit card readers', 'Receipt printers', 'Label printers',
      'Handheld devices', 'Mobile devices', 'Wireless devices', 'Bluetooth devices',
      'USB devices', 'External hard drives', 'Network storage devices', 'Servers',
      'Routers', 'Switches', 'Firewalls', 'VPN devices', 'Wireless access points',
      'Network cables', 'Fiber optic cables', 'Coaxial cables', 'Telephone cables',
      'Power supplies', 'Uninterruptible power supplies', 'Surge protectors', 'Batteries',
      'Chargers', 'Adapters', 'Converters', 'Splitters', 'Hubs', 'Docks', 'Stands'
    ];
  };

  // Get dynamic counts
  const skillsCount = getSkillsData().length;
  const knowledgeCount = getKnowledgeData().length;
  const technologyCount = getTechnologyData().length;
  const toolsCount = getToolsData().length;

  // Dynamic employment projections data
  const getEmploymentProjections = () => {
    if (onetData?.employment_projections) {
      return onetData.employment_projections;
    }
    // Fallback to job data
    return {
      currentEmployment: job.employment,
      projectedEmployment: job.projectedEmployment,
      employmentChange: job.employmentChange,
      employmentPercentChange: job.employmentPercentChange,
      currentYear: 2023,
      projectedYear: 2026
    };
  };

  const employmentData = getEmploymentProjections();

  // Get ONET job outlook data
  const getJobOutlook = () => {
    if (onetData?.job_outlook) {
      return onetData.job_outlook;
    }
    return null;
  };

  const jobOutlook = getJobOutlook();

  // Get ONET abilities data
  const getAbilities = () => {
    if (onetData?.abilities) {
      return onetData.abilities;
    }
    return [];
  };

  const abilities = getAbilities();

  return (
    <div className="space-y-4">
        {/* Hero Section - Job Overview */}
        <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] p-8 shadow-sm">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
          </div>

        </div>

        {/* Career Cluster Tag with Quick Actions */}
        {job.careerCluster && (
          <div className="mb-4">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-[#f2e8d5] text-[#5a483b] border border-[#c1a27b] font-['Roboto']">
              {job.careerCluster}
            </span>
            <div className="flex flex-wrap gap-2 mt-3">
              <button 
                onClick={() => {
                  const tourElement = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
                  if (!tourElement) {
                    window.dispatchEvent(new CustomEvent('viewJobPostings', { 
                      detail: { socCode: job.socCode, jobTitle: job.title }
                    }));
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-[#0ea5e9] to-[#0284c7] text-[#ffffff] rounded-lg hover:from-[#0284c7] hover:to-[#0ea5e9] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] cursor-pointer job-postings-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
                </svg>
                Job Postings
              </button>
              <button 
                onClick={() => {
                  // Check if demo tour is active
                  const tourElement = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
                  if (!tourElement) {
                    // This will be handled by the parent component - trigger businesses view
                    window.dispatchEvent(new CustomEvent('viewBusinesses', { 
                      detail: { socCode: job.socCode, jobTitle: job.title } 
                    }));
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-[#1b2c5a] to-[#162844] text-[#ffffff] rounded-lg hover:from-[#162844] hover:to-[#1b2c5a] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] cursor-pointer businesses-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                View Businesses
              </button>
              
              <button 
                onClick={() => {
                  // Check if demo tour is active
                  const tourElement = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
                  if (!tourElement) {
                    // This will be handled by the parent component - trigger industries view
                    window.dispatchEvent(new CustomEvent('viewIndustries', { 
                      detail: { socCode: job.socCode, jobTitle: job.title } 
                    }));
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-[#76b6f2] to-[#5a9bd8] text-[#ffffff] rounded-lg hover:from-[#5a9bd8] hover:to-[#76b6f2] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] cursor-pointer industries-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                View Industries
              </button>
              
              <button 
                onClick={() => {
                  // Check if demo tour is active
                  const tourElement = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
                  if (!tourElement) {
                    // This will be handled by the parent component
                    window.dispatchEvent(new CustomEvent('showFinancialAid'));
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-[#d5a32a] to-[#f2c94c] text-[#ffffff] rounded-lg hover:from-[#f2c94c] hover:to-[#d5a32a] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto'] cursor-pointer financial-aid-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Financial Aid
              </button>



              {job.oohUrl && (
                <a
                  href={job.oohUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-[#f2994a] to-[#d5a32a] text-[#ffffff] rounded-lg hover:from-[#d5a32a] hover:to-[#f2994a] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Learn More
                </a>
              )}
            </div>
          </div>
        )}

        {/* Job Description */}
        <div className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm">
          <h3 className="mx-0 mt-0 mb-2 text-lg font-medium leading-tight text-zinc-700">Job Description</h3>
          <p className="text-[#1b2c5a] leading-relaxed text-base font-['Roboto']">
            {onetData?.description || job.description || "Receive and disburse money in establishments other than financial institutions. May use electronic scanners, cash registers, or related equipment. May process credit or debit card transactions and validate checks."}
          </p>
        </div>

        {/* ONET Tasks - Show if available */}
        {onetData?.tasks && onetData.tasks.length > 0 && (
          <div className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm">
            <h3 className="mx-0 mt-0 mb-3 text-lg font-medium leading-tight text-zinc-700">Key Tasks</h3>
            <div className="space-y-2">
              {onetData.tasks.slice(0, 5).map((task: any, index: number) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-[#d5a32a] rounded-full mt-2"></div>
                  <p className="text-[#1b2c5a] text-sm font-['Roboto']">
                    {typeof task === 'string' ? task : task.name || task.description || 'Task description not available'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}


      </div>

      {/* Key Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[#d5a32a]">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="flex items-center gap-2" style={{ position: 'relative' }}>
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-[#707070] uppercase tracking-wide font-['Roboto']">
                  {showMedianSalary ? 'Median Annual Salary' : 'Annual Salary Range'}
                </span>
                {!isMobile && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center cursor-pointer border-0 bg-transparent p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowColTooltip(!showColTooltip);
                      setIsTooltipOpen(!showColTooltip);
                    }}
                    onMouseEnter={() => {
                      setShowColTooltip(true);
                      setIsTooltipOpen(true);
                    }}
                    onMouseLeave={() => {
                      setShowColTooltip(false);
                      setIsTooltipOpen(false);
                    }}
                    onFocus={() => {
                      setShowColTooltip(true);
                      setIsTooltipOpen(true);
                    }}
                    onBlur={() => {
                      setShowColTooltip(false);
                      setIsTooltipOpen(false);
                    }}
                    aria-label={showMedianSalary ? "Median Salary Information" : "Salary Range Information"}
                    tabIndex={0}
                  >
                    <svg className="w-3.5 h-3.5 text-[#d5a32a]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Desktop Tooltip */}
              {showColTooltip && !isMobile && (
                <div 
                  className="col-tooltip-container absolute left-0 top-full mt-2 z-[99999] w-72 p-3 bg-white border-2 border-[#d5a32a] rounded-lg shadow-xl text-xs text-gray-700"
                  style={{ whiteSpace: 'normal' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#1b2c5a' }}>
                    {showMedianSalary ? 'Cost of Living (COL) Salary' : 'Salary Range (10th–90th percentile)'}
                  </strong>
                  <p style={{ margin: 0, lineHeight: '1.5' }}>
                    {showMedianSalary 
                      ? 'This number shows what a typical paycheck is worth in Sumner County after adjusting for local costs, so you can see how far your money really goes here compared to other places.'
                      : 'This range shows salaries from the 10th percentile (lowest earners) to 90th percentile (highest earners) in Sumner County.'}
                  </p>
                  <div className="absolute -top-2 left-4 w-3 h-3 bg-white border-t-2 border-l-2 border-[#d5a32a] transform rotate-45"></div>
                </div>
              )}
              <button
                onClick={() => setShowMedianSalary(!showMedianSalary)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#d5a32a] focus:ring-offset-2 ${
                  showMedianSalary ? 'bg-[#d5a32a]' : 'bg-gray-200'
                }`}
                title={`Toggle between ${showMedianSalary ? 'Annual Salary' : 'Cost of Living (COL) Annual Salary'} view`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    showMedianSalary ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          <p className="text-2xl font-bold text-[#d5a32a] font-['Roboto']">
            {showMedianSalary ? formatSalary(job.estimatedSalary) : getAnnualSalaryRange()}
          </p>
          
          {/* Mobile: Tappable Salary Explanation Row */}
          {isMobile && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowColTooltip(true);
                setIsTooltipOpen(true);
              }}
              className="mt-2 w-full text-left px-3 py-2 bg-[#f8f9fa] border border-[#d5a32a] rounded-lg flex items-center gap-2 hover:bg-[#fff8e1] transition-colors"
            >
              <svg className="w-4 h-4 text-[#d5a32a] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-[#1b2c5a] font-medium">{showMedianSalary ? 'Cost of living explained' : 'What is salary range?'}</span>
            </button>
          )}
        </div>

        <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#76b6f2]">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-[#707070] uppercase tracking-wide font-['Roboto']">
              {showMedianSalary ? 'Median Hourly Wage' : 'Hourly Wage Range'}
            </span>
          </div>
          <p className="text-xl font-bold text-[#76b6f2] font-['Roboto']">
            {showMedianSalary ? formatWage(job.medianWage) : getHourlyRange()}
          </p>
        </div>

        <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#162844]">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-[#707070] uppercase tracking-wide font-['Roboto']">Current Employment</span>
          </div>
          <p className="text-xl font-bold text-[#162844] font-['Roboto']">
            {formatEmployment(job.employment)}
          </p>
        </div>

        <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#f2994a]">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <span className="text-xs font-medium text-[#707070] uppercase tracking-wide font-['Roboto']">Growth Trend</span>
          </div>
          <div className="flex items-center">
            {getEmploymentTrendIcon()}
            <span className={`text-xl font-bold ml-2 font-['Roboto'] ${
              getEmploymentTrend() === 'positive' ? 'text-[#f2994a]' :
              getEmploymentTrend() === 'negative' ? 'text-[#707070]' : 'text-[#a6a6a6]'
            }`}>
              {job.employmentPercentChange ? `${Math.round(job.employmentPercentChange)}%` : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left Column - Detailed Job Information */}
        <div className="lg:col-span-2 space-y-3">
          {/* Detailed Job Information */}
          <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] shadow-sm job-detail-modal">
            <div className="p-4 border-b border-[#e6e6e6]">
              <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">Required Skills & Knowledge</h3>
              <p className="text-[#707070] mt-1 font-['Roboto']">Essential skills and knowledge areas for this position</p>
            </div>
            <div className="p-4">
                              <div className="space-y-4">
                  {/* Tab Navigation */}
                  <div className="flex flex-wrap gap-2 border-b border-[#e6e6e6] pb-3">
                  <button 
                    onClick={() => handleTabClick('skills')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 font-['Roboto'] cursor-pointer ${
                      activeTab === 'skills' 
                        ? 'bg-[#1b2c5a] text-[#ffffff] shadow-sm' 
                        : 'bg-[#ffffff] text-[#1b2c5a] hover:bg-[#c4d7f2] border border-[#e6e6e6]'
                    }`}
                  >
                    Skills ({skillsCount})
                  </button>
                  <button 
                    onClick={() => handleTabClick('knowledge')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 font-['Roboto'] cursor-pointer ${
                      activeTab === 'knowledge' 
                        ? 'bg-[#1b2c5a] text-[#ffffff] shadow-sm' 
                        : 'bg-[#ffffff] text-[#1b2c5a] hover:bg-[#c4d7f2] border border-[#e6e6e6]'
                    }`}
                  >
                    Knowledge ({knowledgeCount})
                  </button>
                  <button 
                    onClick={() => handleTabClick('technology')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 font-['Roboto'] cursor-pointer ${
                      activeTab === 'technology' 
                        ? 'bg-[#1b2c5a] text-[#ffffff] shadow-sm' 
                        : 'bg-[#ffffff] text-[#1b2c5a] hover:bg-[#c4d7f2] border border-[#e6e6e6]'
                    }`}
                  >
                    Technology ({technologyCount})
                  </button>
                  <button 
                    onClick={() => handleTabClick('tools')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 font-['Roboto'] cursor-pointer ${
                      activeTab === 'tools' 
                        ? 'bg-[#1b2c5a] text-[#ffffff] shadow-sm' 
                        : 'bg-[#ffffff] text-[#1b2c5a] hover:bg-[#c4d7f2] border border-[#e6e6e6]'
                    }`}
                  >
                    Tools ({toolsCount})
                  </button>
                  {abilities.length > 0 && (
                    <button 
                      onClick={() => handleTabClick('abilities')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 font-['Roboto'] cursor-pointer ${
                        activeTab === 'abilities' 
                          ? 'bg-[#1b2c5a] text-[#ffffff] shadow-sm' 
                          : 'bg-[#ffffff] text-[#1b2c5a] hover:bg-[#c4d7f2] border border-[#e6e6e6]'
                      }`}
                    >
                      Abilities ({abilities.length})
                    </button>
                  )}
                </div>

                {/* Tab Content */}
                <div className="min-h-[400px] max-h-[500px] overflow-y-auto">
                  {activeTab === 'skills' && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-[#1b2c5a] text-lg font-['Roboto_Condensed']">Required Skills</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {getSkillsData().map((skill: string, index: number) => (
                          <div key={index} className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors shadow-sm">
                            <span className="text-sm font-medium text-[#1b2c5a] font-['Roboto']">{skill}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'knowledge' && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-[#1b2c5a] text-lg font-['Roboto_Condensed']">Required Knowledge</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {getKnowledgeData().map((knowledge: string, index: number) => (
                          <div key={index} className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors shadow-sm">
                            <span className="text-sm font-medium text-[#1b2c5a] font-['Roboto']">{knowledge}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'technology' && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-[#1b2c5a] text-lg font-['Roboto_Condensed']">Required Technology</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {getTechnologyData().map((tech: string, index: number) => (
                          <div key={index} className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] hover:border-[#76b6f2] transition-colors shadow-sm">
                            <span className="text-sm font-medium text-[#1b2c5a] font-['Roboto']">{tech}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'tools' && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-[#1b2c5a] text-lg font-['Roboto_Condensed']">Required Tools</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {getToolsData().map((tool: string, index: number) => (
                          <div key={index} className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] hover:border-[#76b6f2] transition-colors shadow-sm">
                            <span className="text-sm font-medium text-[#1b2c5a] font-['Roboto']">{tool}</span>
                          </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'abilities' && abilities.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-[#1b2c5a] text-lg font-['Roboto_Condensed']">Required Abilities</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {abilities.map((ability: any, index: number) => (
                          <div key={index} className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] hover:border-[#d5a32a] transition-colors shadow-sm">
                            <div className="space-y-1">
                              <span className="text-sm font-medium text-[#1b2c5a] font-['Roboto']">
                                {typeof ability === 'string' ? ability : ability.name || 'Unknown Ability'}
                              </span>
                              {ability.description && (
                                <p className="text-xs text-[#707070] font-['Roboto']">{ability.description}</p>
                              )}
                              {ability.importance && (
                                <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                  {ability.importance}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


        </div>

        {/* Right Column - Additional Information */}
        <div className="space-y-8">

          {/* Education & Requirements */}
          <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] shadow-sm h-fit">
            <div className="p-4 border-b border-[#e6e6e6]">
              <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">Education & Requirements</h3>
            </div>
            <div className="p-4 overflow-hidden">
              <div>
                <p className="text-sm font-medium text-[#707070] mb-0.5 font-['Roboto']">Education Requirement</p>
                <p className="text-[#1b2c5a] font-medium font-['Roboto']">
                  {onetData?.job_zone?.education || job.education || job.educationRequirement || "No formal educational credential"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#707070] mb-0.5 font-['Roboto']">Work Experience</p>
                <p className="text-[#1b2c5a] font-medium font-['Roboto']">
                  {onetData?.job_zone?.experience || job.workExperience || "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#707070] mb-0.5 font-['Roboto']">On-the-Job Training</p>
                <p className="text-[#1b2c5a] font-medium font-['Roboto']">
                  {job.onJobTraining || "Short-term on-the-job training"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#707070] mb-0.5 font-['Roboto']">Job Zone</p>
                <p className="text-[#1b2c5a] font-medium font-['Roboto']">
                  {onetData?.job_zone?.name ? `${onetData.job_zone.name} (Zone ${onetData.job_zone.zone})` : "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#707070] mb-0.5 font-['Roboto']">Job Posting Duration</p>
                <p className="text-[#1b2c5a] font-medium font-['Roboto']">
                  {job.jobPostingDuration ? `${job.jobPostingDuration} days` : "28 days"}
                </p>
              </div>
            </div>
          </div>

          {/* Employment Projections */}
          <div className="bg-[#ffffff] rounded-xl border border-[#e6e6e6] shadow-sm h-fit">
            <div className="p-4 border-b border-[#e6e6e6]">
              <h3 className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700">Employment Projections</h3>
              <p className="text-[#707070] mt-1 font-['Roboto']">Current and projected employment data</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm">
                  <h4 
                    className="text-sm font-medium text-[#1b2c5a] mb-2 font-['Roboto'] cursor-help"
                    title="Current number of people employed in this occupation nationwide"
                  >
                    Current Employment ({employmentData.currentYear})
                  </h4>
                  <p 
                    className="text-2xl font-bold text-[#1b2c5a] font-['Roboto'] hover:underline cursor-pointer transition-colors"
                    title="Click to view detailed employment breakdown by industry and region"
                    onClick={() => {}}
                  >
                    {formatEmployment(employmentData.currentEmployment)}
                  </p>
                </div>
                <div className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm">
                  <h4 
                    className="text-sm font-medium text-[#1b2c5a] mb-2 font-['Roboto'] cursor-help"
                    title="Projected number of people employed in this occupation by the target year"
                  >
                    Projected Employment ({employmentData.projectedYear})
                  </h4>
                  <p 
                    className="text-2xl font-bold text-[#1b2c5a] font-['Roboto'] hover:underline cursor-pointer transition-colors"
                    title="Click to view detailed projection methodology and growth factors"
                    onClick={() => {}}
                  >
                    {formatEmployment(employmentData.projectedEmployment)}
                  </p>
                </div>
                <div className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm">
                  <h4 
                    className="text-sm font-medium text-[#1b2c5a] mb-2 font-['Roboto'] cursor-help"
                    title="Net change in employment between current and projected years"
                  >
                    Employment Change
                  </h4>
                  <p 
                    className={`text-2xl font-bold font-['Roboto'] hover:underline cursor-pointer transition-colors ${
                      employmentData.employmentChange && employmentData.employmentChange > 0 ? 'text-[#f2994a]' :
                      employmentData.employmentChange && employmentData.employmentChange < 0 ? 'text-[#707070]' : 'text-[#a6a6a6]'
                    }`}
                    title="Click to view detailed analysis of employment change factors"
                    onClick={() => {}}
                  >
                    {employmentData.employmentChange ? (employmentData.employmentChange > 0 ? '+' : '') + Math.round(employmentData.employmentChange).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div className="bg-[#ffffff] rounded-lg p-4 border border-[#e6e6e6] shadow-sm">
                  <h4 
                    className="text-sm font-medium text-[#1b2c5a] mb-2 font-['Roboto'] cursor-help"
                    title="Percentage change in employment between current and projected years"
                  >
                    Percent Change
                  </h4>
                  <p 
                    className={`text-2xl font-bold font-['Roboto'] hover:underline cursor-pointer transition-colors ${
                      employmentData.employmentPercentChange && employmentData.employmentPercentChange > 0 ? 'text-[#f2994a]' :
                      employmentData.employmentPercentChange && employmentData.employmentPercentChange < 0 ? 'text-[#707070]' : 'text-[#a6a6a6]'
                    }`}
                    title="Click to view detailed percentage analysis and growth trends"
                    onClick={() => {}}
                  >
                    {employmentData.employmentPercentChange ? (employmentData.employmentPercentChange > 0 ? '+' : '') + employmentData.employmentPercentChange.toFixed(1) + '%' : 'N/A'}
                  </p>
                </div>
              </div>



              {/* ONET Job Outlook - Show if available */}
              {jobOutlook && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-[#1b2c5a] mb-2 font-['Roboto']">Job Outlook</h4>
                  <div className="space-y-2">
                    {jobOutlook.bright_outlook && (
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          jobOutlook.bright_outlook === 'Yes' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {jobOutlook.bright_outlook === 'Yes' ? 'Bright Outlook' : 'Standard Outlook'}
                        </span>
                      </div>
                    )}
                    {jobOutlook.outlook_text && (
                      <p className="text-sm text-[#1b2c5a] font-['Roboto']">{jobOutlook.outlook_text}</p>
                    )}
                    {jobOutlook.growth_rate && (
                      <p className="text-sm text-[#707070] font-['Roboto']">
                        Growth Rate: <span className="font-medium">{jobOutlook.growth_rate}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Enhanced Geographic Comparison */}
      <GeographicComparison 
        socCode={job.socCode} 
        jobTitle={job.title} 
      />

      {/* Mobile Bottom Sheet for COL Explanation */}
      {showColTooltip && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={() => setShowColTooltip(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px 16px 0 0',
              padding: '24px',
              width: '100%',
              maxWidth: '600px',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(0)',
              transition: 'transform 0.3s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '4px', backgroundColor: '#d5d5d5', borderRadius: '2px' }} />
            </div>
            
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1b2c5a', marginBottom: '12px' }}>
              {showMedianSalary ? 'Cost of Living (COL) Salary' : 'Salary Range (10th–90th percentile)'}
            </h3>
            <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#333', marginBottom: '16px' }}>
              {showMedianSalary 
                ? 'This number shows what a typical paycheck is worth in Sumner County after adjusting for local costs, so you can see how far your money really goes here compared to other places.'
                : 'This range shows salaries from the 10th percentile (lowest earners) to 90th percentile (highest earners) in Sumner County.'}
            </p>
            <button
              onClick={() => {
                setShowColTooltip(false);
                setIsTooltipOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#d5a32a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescription; 